import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ric } from "@/lib/ric";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Package, ArrowRightLeft, Truck, Users, ClipboardCheck, AlertTriangle } from "lucide-react";
import { MyKRA } from "@/pages/admin/AdminWidgets";

// RIC dashboard — Hindi-first per spec. Same design language as HAG, but the
// tile copy defaults to Hindi with an EN toggle inline.
export default function RICDashboard() {
  const { user } = useAuth();
  const { lang } = useLang();
  const t = (en, hi) => (lang === "en" ? en : hi);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      const [pos, disc, mv] = await Promise.all([ric.listPOs(), ric.discrepancies(), ric.movements(500)]);
      const monthAgo = Date.now() - 30 * 24 * 3600e3;
      const desp_month = mv.filter((m) => m.kind === "despatch_mg" && new Date(m.created_at) > monthAgo)
                          .reduce((a, b) => a + b.kg, 0);
      const sales_month = mv.filter((m) => m.kind === "sale_weaver" && new Date(m.created_at) > monthAgo)
                          .reduce((a, b) => a + b.kg, 0);
      setStats({
        open_pos: pos.filter((p) => !["Closed", "Cancelled", "Rejected"].includes(p.status)).length,
        open_pos_kg: pos.filter((p) => !["Closed", "Cancelled", "Rejected"].includes(p.status))
                        .reduce((a, p) => a + p.lines.reduce((x, ln) => x + (ln.kg_requested - (ln.kg_received || 0)), 0), 0),
        desp_month, sales_month,
        open_disc: disc.filter((d) => d.status === "Open").length,
      });
    })();
  }, []);

  const tiles = [
    { to: "/ric/receive", icon: Package, en: "Receive yarn", hi: "यार्न प्राप्त करें", testid: "ric-tile-receive" },
    { to: "/ric/transfer", icon: ArrowRightLeft, en: "Bin transfer", hi: "बिन ट्रांसफर", testid: "ric-tile-transfer" },
    { to: "/ric/po-queue", icon: Truck, en: "MG PO queue", hi: "MG PO लाइन", testid: "ric-tile-poqueue" },
    { to: "/ric/weaver-sale", icon: Users, en: "Weaver sale", hi: "बुनकर बिक्री", testid: "ric-tile-sale" },
    { to: "/ric/count", icon: ClipboardCheck, en: "Physical count", hi: "फिज़िकल गिनती", testid: "ric-tile-count" },
    { to: "/ric/stock", icon: Package, en: "Stock browser", hi: "स्टॉक ब्राउज़र", testid: "ric-tile-stock" },
  ];

  return (
    <div data-testid="ric-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-1">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>
            {t("Royal Indian Carpets · Godown", "रॉयल इंडियन कारपेट्स · गोदाम")}
          </h1>
          <p className="text-xs text-[#7C8E9E] mt-1">
            {t("1698/1, Jangi Road, Mirzapur — welcome, ", "1698/1, जंगी रोड, मिर्ज़ापुर — नमस्ते, ")}
            <b>{user?.name}</b>
          </p>
        </div>
      </div>

      <div className="mb-4"><MyKRA /></div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 mb-6">
          <MiniStat label={t("Open POs (count)", "खुले PO")} v={stats.open_pos} testid="ric-stat-openpos" />
          <MiniStat label={t("Open PO kg", "खुले PO कि.")} v={stats.open_pos_kg.toFixed(1)} testid="ric-stat-openposkg" />
          <MiniStat label={t("Despatched (30d)", "भेजा (30 दिन)")} v={`${stats.desp_month.toFixed(1)} kg`} testid="ric-stat-desp" />
          <MiniStat label={t("Weaver sales (30d)", "बुनकर बिक्री")} v={`${stats.sales_month.toFixed(1)} kg`} testid="ric-stat-sales" />
        </div>
      )}

      {stats?.open_disc > 0 && (
        <Link to="/ric/discrepancies" className="hag-surface p-3 mb-4 flex items-center gap-2 border-l-4 border-red-500 hover:bg-red-50" data-testid="ric-disc-banner">
          <AlertTriangle size={16} className="text-red-600" />
          <span className="text-sm">
            <b>{stats.open_disc}</b> {t("open discrepancy tickets — please review", "खुले विसंगति टिकट — कृपया जाँचें")}
          </span>
        </Link>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {tiles.map(({ to, icon: Icon, en, hi, testid }) => (
          <Link key={to} to={to} data-testid={testid} className="hag-surface p-5 hover:bg-[#FAF6F0] flex items-center gap-3">
            <Icon size={28} className="text-[#C8A86B]" />
            <div>
              <div className="text-lg font-bold" style={{ color: "var(--brand-navy)" }}>{lang === "en" ? en : hi}</div>
              <div className="text-xs text-[#7C8E9E]">{lang === "en" ? hi : en}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function MiniStat({ label, v, testid }) {
  return (
    <div className="hag-surface p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#7C8E9E]">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color: "var(--brand-navy)" }}>{v}</div>
    </div>
  );
}
